import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../../Common/DatasetSelector';
import DiscretizationLayout from '../../../Common/DiscretizationLayout';

const LineChart = ({ plotData, thresholds, defaultTitle, xlabel, ylabel, titleKey }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => localStorage.getItem(`dwt_title_${titleKey}`) || defaultTitle || '');
  const [showEditor, setShowEditor] = useState(false);
  const [showTable, setShowTable] = useState(false);

  const renderPlot = (t) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const x = plotData.map(d => d.date);
    const y = plotData.map(d => d.value);
    const traces = [{ x, y, type: 'scatter', mode: 'lines+markers', name: 'Factor', line: { width: 2 }, marker: { size: 4 } }];
    if (thresholds) {
      const isTroughs = thresholds.severity === "Troughs";
      // Always: smaller value = Low line, larger value = High line (just like the tables)
      // Pics: Low(green/OK) High(red/bad) | Troughs: Low(red/bad) High(green/OK)
      const lowVal = Math.min(thresholds.q_low ?? Infinity, thresholds.q_high ?? Infinity);
      const highVal = Math.max(thresholds.q_low ?? -Infinity, thresholds.q_high ?? -Infinity);
      if (highVal != null) traces.push({ x: [x[0], x[x.length-1]], y: [highVal, highVal], type: 'scatter', mode: 'lines', name: 'High', line: { dash: 'dash', color: isTroughs ? 'green' : 'red', width: 2 } });
      if (lowVal != null) traces.push({ x: [x[0], x[x.length-1]], y: [lowVal, lowVal], type: 'scatter', mode: 'lines', name: 'Low', line: { dash: 'dash', color: isTroughs ? 'red' : 'green', width: 2 } });
    }
    window.Plotly.newPlot(plotRef.current, traces, { title: t, xaxis: { title: xlabel }, yaxis: { title: ylabel }, template: 'plotly_white', height: 450, legend: { orientation: 'h', y: -0.3 }, margin: { t: 40, b: 80, l: 60, r: 20 } }, { responsive: true });
  };

  useEffect(() => { if (plotData && plotData.length > 0) renderPlot(title); }, [plotData, title, thresholds]);
  if (!plotData || plotData.length === 0) return <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>No data available.</div>;

  return (
    <div style={{ marginBottom: '20px', background: 'white', borderRadius: '8px', padding: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px' }}>{title}</h4>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={() => setShowTable(!showTable)} style={{ padding: '2px 8px', fontSize: '11px', background: '#34495e', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>{showTable ? 'Hide' : 'Table'}</button>
          <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '2px 8px', fontSize: '11px', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>✏️</button>
        </div>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', gap: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px', marginBottom: '8px' }}>
          <input placeholder="Title" value={title} onChange={e => setTitle(e.target.value)} style={{ flex: 1, fontSize: '12px', padding: '4px' }} />
          <button onClick={() => { renderPlot(title); localStorage.setItem(`dwt_title_${titleKey}`, title); setShowEditor(false); }} style={{ background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '450px' }}></div>
      {showTable && (
        <div style={{ marginTop: '12px', maxHeight: '250px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
          <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white' }}>
              <tr>{Object.keys(plotData[0] || {}).map(h => <th key={h} style={{ padding: '6px' }}>{h}</th>)}</tr>
            </thead>
            <tbody>{plotData.map((row, i) => (<tr key={i} style={{ borderBottom: '1px solid #eee' }}>{Object.keys(plotData[0] || {}).map(h => <td key={h} style={{ padding: '4px' }}>{typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}</td>)}</tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const DwtIdentification = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [selectedFactor, setSelectedFactor] = useState(null);
  const [groups, setGroups] = useState({ params: false });
  const [formData, setFormData] = useState({ date_period: null, factor_eco: [], severity_sens: { value: 'Pics', label: 'Pics' }, transformations: { value: 'Value', label: 'Value' }, quantile: 95 });

  const severityOptions = [{ value: 'Pics', label: 'Pics' }, { value: 'Troughs', label: 'Troughs' }];
  const transformOptions = [{ value: 'Value', label: 'Value' }, { value: 'Relative variation', label: 'Relative variation' }, { value: 'Absolute variation', label: 'Absolute variation' }];
  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => { if (!window.Plotly) { const s = document.createElement('script'); s.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js'; document.head.appendChild(s); } }, []);

  const hdl = (data) => { setColumns(data.columns || []); setDatasetInfo(data); setDatasetLoaded(true); toast.success('Loaded'); };
  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));
  const toggleGroup = (g) => setGroups(p => ({ ...p, [g]: !p[g] }));

  // Get current factor result
  const currentResult = results?.all_results?.find(r => r.factor_name === selectedFactor?.value) || results?.all_results?.[0];
  const factorOptions = (results?.all_results || []).map(r => ({ value: r.factor_name, label: r.factor_name }));

  // Auto-select first factor when results load
  useEffect(() => {
    if (results?.all_results?.length > 0 && !selectedFactor) {
      setSelectedFactor({ value: results.all_results[0].factor_name, label: results.all_results[0].factor_name });
    }
  }, [results]);

  const saveParams = async () => {
    if (!formData.date_period || formData.factor_eco.length === 0) { toast.error('Select Date Period and at least one Economic Factor'); return; }
    setLoading(true);
    try {
      await axios.post('/api/dwt-identification/step0', {
        date_period: formData.date_period.value,
        factor_eco: formData.factor_eco.map(f => f.value),
        severity_sens: formData.severity_sens.value,
        transformations: formData.transformations.value,
        quantile: formData.quantile
      });
      toast.success('Parameters saved'); setStep(1);
    } catch (err) { toast.error('Failed'); } finally { setLoading(false); }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const r = await axios.post('/api/dwt-identification/step1', {
        date_period: formData.date_period.value,
        factor_eco: formData.factor_eco.map(f => f.value),
        severity_sens: formData.severity_sens.value,
        transformations: formData.transformations.value,
        quantile: formData.quantile
      });
      setResults(r.data.results); toast.success(r.data.message); setStep(2);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setLoading(false); }
  };

  const exportResults = async () => {
    try {
      const ct = { title: localStorage.getItem('dwt_title_dwt') || '' };
      const r = await axios.post('/api/dwt-identification/export-with-titles', ct, { responseType: 'blob' }).catch(() => axios.get('/api/dwt-identification/export', { responseType: 'blob' }));
      const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'dwt_results.xlsx'; a.click(); window.URL.revokeObjectURL(u);
    } catch (err) { toast.error('Export failed'); }
  };

  const sc = (t) => { if (t === 0) setStep(0); else if (t === 1 && datasetLoaded) setStep(1); else if (t === 2 && results) setStep(2); };
  const cs = { control: (b) => ({ ...b, minHeight: '36px', fontSize: '13px' }), menu: (b) => ({ ...b, zIndex: 9999 }), menuPortal: (b) => ({ ...b, zIndex: 9999 }) };
  const gs = { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '16px' };
  const ghs = { background: '#f5f7fa', padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontWeight: 600, fontSize: '14px' };
  const gcs = { padding: '16px', background: 'white' };

  return (
    <DiscretizationLayout title="Dwt Identification" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      {step === 0 && (<>
        <DatasetSelector apiPrefix="/api/dwt-identification" onDatasetLoaded={hdl} />
        {columns.length > 0 && (<div style={{ marginTop: '20px' }}>
          <div style={gs}><div style={ghs} onClick={() => toggleGroup('params')}><span>📊 Parameters</span><span>{groups.params ? '−' : '+'}</span></div>
            {groups.params && <div style={gcs}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Date Period *</label><Select options={columnOptions} value={formData.date_period} onChange={v => hfc('date_period', v)} styles={cs} menuPortalTarget={document.body} /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Economic Factors *</label><Select options={columnOptions} isMulti value={formData.factor_eco} onChange={v => hfc('factor_eco', v)} styles={cs} menuPortalTarget={document.body} placeholder="Select one or more factors..." /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Severity</label><Select options={severityOptions} value={formData.severity_sens} onChange={v => hfc('severity_sens', v)} styles={cs} menuPortalTarget={document.body} /></div>
                <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Transformation</label><Select options={transformOptions} value={formData.transformations} onChange={v => hfc('transformations', v)} styles={cs} menuPortalTarget={document.body} /></div>
              </div>
              <div><label style={{ fontSize: '12px', fontWeight: 500 }}>Quantile (%)</label><input type="number" value={formData.quantile} onChange={e => hfc('quantile', parseInt(e.target.value))} style={{ width: '200px', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px' }} /></div>
            </div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button onClick={saveParams} disabled={loading} style={{ padding: '8px 28px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}>{loading ? 'Saving...' : 'Continue →'}</button>
          </div>
        </div>)}
      </>)}

      {step === 1 && (<div>
        <div style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Review Parameters</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
            <p><strong>Date:</strong> {formData.date_period?.label}</p>
            <p><strong>Factors:</strong> {formData.factor_eco?.map(f => f.label).join(', ') || 'None'}</p>
            <p><strong>Severity:</strong> {formData.severity_sens?.label}</p>
            <p><strong>Transform:</strong> {formData.transformations?.label}</p>
            <p><strong>Quantile:</strong> {formData.quantile}%</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => setStep(0)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
          <button onClick={runAnalysis} disabled={loading} style={{ padding: '8px 24px', background: loading ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}>{loading ? 'Running...' : 'Run Analysis →'}</button>
        </div>
      </div>)}

      {step === 2 && results && currentResult && (
        <div>
          {/* Factor selector dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
            <span style={{ fontSize: '10px', fontWeight: 500, color: '#888' }}>Factor:</span>
            <div style={{ width: '160px' }}>
              <Select 
                options={factorOptions} 
                value={selectedFactor} 
                onChange={setSelectedFactor} 
                styles={{ 
                  control: (b) => ({ ...b, minHeight: '24px', height: '24px', fontSize: '10px', borderColor: '#ddd', borderRadius: '3px' }),
                  valueContainer: (b) => ({ ...b, padding: '0 4px', height: '24px' }),
                  input: (b) => ({ ...b, fontSize: '10px', margin: 0, padding: 0 }),
                  placeholder: (b) => ({ ...b, fontSize: '10px', color: '#bbb' }),
                  singleValue: (b) => ({ ...b, fontSize: '10px' }),
                  dropdownIndicator: (b) => ({ ...b, padding: '0 3px' }),
                  indicatorSeparator: () => ({ display: 'none' }),
                  menu: (b) => ({ ...b, zIndex: 9999, fontSize: '10px', width: '160px' }),
                  option: (b) => ({ ...b, fontSize: '10px', padding: '4px 8px' }),
                  menuPortal: (b) => ({ ...b, zIndex: 9999 })
                }} 
                menuPortalTarget={document.body} 
              />
            </div>
            {currentResult && (
              <span style={{ fontSize: '9px', color: '#aaa' }}>
                {results.meta?.severity_sens === 'Troughs' ? (
                  <>🔴{currentResult.meta?.n_low || 0} 🟢{currentResult.meta?.n_high || 0}</>
                ) : (
                  <>🟢{currentResult.meta?.n_low || 0} 🔴{currentResult.meta?.n_high || 0}</>
                )}
              </span>
            )}
          </div>

          <LineChart 
            key={`${currentResult.factor_name}_${results.meta?.severity_sens}`}
            plotData={currentResult.line_data} 
            thresholds={{ q_low: currentResult.meta?.q_low, q_high: currentResult.meta?.q_high, severity: results.meta?.severity_sens }}
            defaultTitle={`${currentResult.factor_name}`}
            xlabel="Date" ylabel={currentResult.factor_name} titleKey="dwt" 
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '20px' }}>
            {currentResult.exceed_low?.length > 0 && (
              <div style={{ background: 'white', borderRadius: '8px', padding: '12px' }}>
                <h4 style={{ color: results.meta?.severity_sens === 'Troughs' ? '#e74c3c' : '#2ecc71' }}>Low Exceedances ({currentResult.exceed_low.length})</h4>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '10px' }}>
                    <thead style={{ background: results.meta?.severity_sens === 'Troughs' ? '#e74c3c' : '#2ecc71', color: 'white' }}>
                      <tr>{Object.keys(currentResult.exceed_low[0] || {}).map(h => <th key={h} style={{ padding: '4px' }}>{h}</th>)}</tr>
                    </thead>
                    <tbody>{currentResult.exceed_low.map((row, i) => (<tr key={i}>{Object.keys(currentResult.exceed_low[0] || {}).map(h => <td key={h} style={{ padding: '3px' }}>{typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}</td>)}</tr>))}</tbody>
                  </table>
                </div>
              </div>
            )}
            {currentResult.exceed_high?.length > 0 && (
              <div style={{ background: 'white', borderRadius: '8px', padding: '12px' }}>
                <h4 style={{ color: results.meta?.severity_sens === 'Troughs' ? '#2ecc71' : '#e74c3c' }}>High Exceedances ({currentResult.exceed_high.length})</h4>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '10px' }}>
                    <thead style={{ background: results.meta?.severity_sens === 'Troughs' ? '#2ecc71' : '#e74c3c', color: 'white' }}>
                      <tr>{Object.keys(currentResult.exceed_high[0] || {}).map(h => <th key={h} style={{ padding: '4px' }}>{h}</th>)}</tr>
                    </thead>
                    <tbody>{currentResult.exceed_high.map((row, i) => (<tr key={i}>{Object.keys(currentResult.exceed_high[0] || {}).map(h => <td key={h} style={{ padding: '3px' }}>{typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}</td>)}</tr>))}</tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => setStep(1)} style={{ padding: '8px 20px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={exportResults} style={{ padding: '8px 20px', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>📥 Export</button>
          </div>
        </div>
      )}
    </DiscretizationLayout>
  );
};

export default DwtIdentification;
